import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  CreditCard, Wallet, Receipt, DollarSign, Clock, FileCheck, BookMarked,
  ClipboardList, AlertTriangle, Plus, Search, Filter, CheckCircle, XCircle,
  Sparkles, Building2, Calendar, Hash, ArrowRight, Play, Pause, Eye, X,
  RefreshCw, Trash2, Send, FileText, Tractor, Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useCompany } from '../context/CompanyContext';
import PageHeader from '../components/layout/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TransactionsPage = () => {
  const location = useLocation();
  const path = location.pathname;

  const getContent = () => {
    if (path.includes('/transactions/quick-check')) return <QuickCheck />;
    if (path.includes('/transactions/manual-check')) return <ManualCheck />;
    if (path.includes('/transactions/ap')) return <AccountsPayable />;
    if (path.includes('/transactions/deposits')) return <Deposits />;
    if (path.includes('/transactions/ar')) return <AccountsReceivable />;
    if (path.includes('/transactions/paychecks')) return <Paychecks />;
    if (path.includes('/transactions/timecards')) return <Timecards />;
    if (path.includes('/transactions/void-checks')) return <VoidChecks />;
    if (path.includes('/transactions/journal')) return <JournalEntries />;
    if (path.includes('/transactions/posting-queue')) return <PostingQueue />;
    if (path.includes('/transactions/ca-farm-payroll')) return <CAFarmPayroll />;
    return <TransactionsOverview />;
  };

  return <div className="space-y-6">{getContent()}</div>;
};

// Overview
const TransactionsOverview = () => {
  const navigate = useNavigate();
  const sections = [
    { 
      title: 'Checks & Payables',
      items: [
        { icon: FileCheck, label: 'Quick Check', path: '/transactions/quick-check' },
        { icon: CreditCard, label: 'Manual Check', path: '/transactions/manual-check' },
        { icon: CreditCard, label: 'Accounts Payable', path: '/transactions/ap' },
      ]
    },
    {
      title: 'Deposits & Receivables',
      items: [
        { icon: Wallet, label: 'Deposits', path: '/transactions/deposits' },
        { icon: Receipt, label: 'Accounts Receivable', path: '/transactions/ar' },
      ]
    },
    {
      title: 'Payroll',
      items: [
        { icon: DollarSign, label: 'Paychecks', path: '/transactions/paychecks' },
        { icon: Clock, label: 'Timecards', path: '/transactions/timecards' },
        { icon: AlertTriangle, label: 'Void Checks', path: '/transactions/void-checks' },
      ]
    },
    {
      title: 'Journal & Posting',
      items: [
        { icon: BookMarked, label: 'Journal Entries', path: '/transactions/journal' },
        { icon: ClipboardList, label: 'Posting Queue', path: '/transactions/posting-queue' },
      ]
    }
  ];

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Transactions</h1>
        <p className="text-gray-500 mt-1">Enter and manage all transaction types</p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {sections.map((section, idx) => (
          <Card key={idx}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">{section.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {section.items.map((item, i) => {
                const Icon = item.icon;
                return (
                  <button
                    key={i}
                    onClick={() => navigate(item.path)}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <Icon className="w-5 h-5 text-slate-600" />
                    </div>
                    <span className="font-medium text-gray-700">{item.label}</span>
                    <ArrowRight className="w-4 h-4 text-gray-400 ml-auto" />
                  </button>
                );
              })}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-gradient-to-r from-purple-50 to-sky-50 border-purple-200">
        <CardContent className="p-4 flex items-center gap-4">
          <Sparkles className="w-8 h-8 text-purple-500" />
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">AI Pre-Post Validation</h3>
            <p className="text-sm text-gray-600">AI checks for missing codes, suspicious totals, and duplicates before posting</p>
          </div>
          <Button variant="outline" className="border-purple-300" onClick={() => navigate('/transactions/posting-queue')}>
            View Queue
          </Button>
        </CardContent>
      </Card>
    </>
  );
};

// ==================== QUICK CHECK ====================
const QuickCheck = () => {
  const { companyId, selectedCompany } = useCompany();
  const [formData, setFormData] = useState({
    payee: '',
    amount: '',
    gl_account: '6000',
    memo: '',
    check_date: new Date().toISOString().split('T')[0],
    enterprise: ''
  });
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [message, setMessage] = useState(null);
  const [companyInfo, setCompanyInfo] = useState({
    name: 'Blue Skies Farms LLC',
    address: '1234 Agricultural Drive',
    city: 'Springfield, IL 62701',
    bank: 'First National Bank',
    routing: '071000505',
    account: '****4892'
  });
  const [nextCheckNumber, setNextCheckNumber] = useState(1001);

  useEffect(() => {
    fetchChecks();
    fetchAccounts();
    fetchNextCheckNumber();
    if (selectedCompany) {
      setCompanyInfo(prev => ({
        ...prev,
        name: selectedCompany.name || prev.name
      }));
    }
  }, [companyId, selectedCompany]);

  const fetchChecks = async () => {
    try {
      const params = new URLSearchParams({ status: 'pending' });
      if (companyId) params.append('company_id', companyId);
      const res = await fetch(`${API_URL}/api/transactions/checks?${params}`, { credentials: 'include' });
      const data = await res.json();
      setChecks(data);
    } catch (err) {
      console.error('Error fetching checks:', err);
    }
  };

  const fetchAccounts = async () => {
    try {
      const params = companyId ? `?company_id=${companyId}` : '';
      const res = await fetch(`${API_URL}/api/transactions/accounts${params}`, { credentials: 'include' });
      const data = await res.json();
      setAccounts(data.length > 0 ? data : [
        { number: '6000', name: 'Salaries & Wages' },
        { number: '6300', name: 'Rent Expense' },
        { number: '6400', name: 'Utilities' },
        { number: '7200', name: 'Farm Expenses' },
        { number: '7500', name: 'Miscellaneous' }
      ]);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setAccounts([
        { number: '6000', name: 'Salaries & Wages' },
        { number: '6300', name: 'Rent Expense' },
        { number: '6400', name: 'Utilities' },
        { number: '7200', name: 'Farm Expenses' },
        { number: '7500', name: 'Miscellaneous' }
      ]);
    }
  };

  const fetchNextCheckNumber = async () => {
    try {
      const params = companyId ? `?company_id=${companyId}` : '';
      const res = await fetch(`${API_URL}/api/transactions/checks/next-number${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setNextCheckNumber(data.next_number);
      }
    } catch (err) {
      console.error('Error fetching next check number:', err);
    }
  };

  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    if (num < 20) return ones[num];
    if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? '-' + ones[num % 10] : '');
    if (num < 1000) return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 ? ' ' + numberToWords(num % 100) : '');
    if (num < 1000000) return numberToWords(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 ? ' ' + numberToWords(num % 1000) : '');
    return num.toString();
  };

  const amountToWords = (amount) => {
    const [dollars, cents] = parseFloat(amount || 0).toFixed(2).split('.');
    const dollarWords = numberToWords(parseInt(dollars));
    return `${dollarWords} and ${cents}/100`;
  };

  const handlePrintCheck = async (checkId) => {
    try {
      toast.loading('Generating check PDF...');
      const res = await fetch(`${API_URL}/api/reports/check-pdf/${checkId}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `check_${checkId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.dismiss();
        toast.success('Check PDF downloaded!');
      } else {
        toast.dismiss();
        toast.error('Failed to generate check PDF');
      }
    } catch (err) {
      toast.dismiss();
      toast.error('Failed to generate check PDF');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const res = await fetch(`${API_URL}/api/transactions/checks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount),
          company_id: companyId
        })
      });

      if (res.ok) {
        const newCheck = await res.json();
        setMessage({ type: 'success', text: `Check #${newCheck.check_number} created for ${formData.payee}` });
        setFormData({ payee: '', amount: '', gl_account: '6000', memo: '', check_date: new Date().toISOString().split('T')[0], enterprise: '' });
        fetchChecks();
      } else {
        throw new Error('Failed to create check');
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to create check' });
    } finally {
      setLoading(false);
    }
  };

  const handleVoid = async (checkId) => {
    try {
      await fetch(`${API_URL}/api/transactions/checks/${checkId}/void`, {
        method: 'POST',
        credentials: 'include'
      });
      fetchChecks();
    } catch (err) {
      console.error('Error voiding check:', err);
    }
  };

  return (
    <>
      <PageHeader
        title="Quick Check"
        subtitle={`${selectedCompany?.name || 'Your company'} · Checks wait in the posting queue until you post them.`}
      />

      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Live Check Preview */}
      <Card className="bg-[#f1f4fa] border border-slate-300 overflow-hidden">
        <div className="relative p-6">
          {/* Check Number */}
          <div className="absolute top-4 right-6 text-right">
            <span className="font-mono text-lg font-bold text-slate-600">No. {nextCheckNumber}</span>
          </div>
          
          {/* Company Header */}
          <div className="mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-sky-600 rounded-lg">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-800">{companyInfo.name}</h3>
                <p className="text-xs text-slate-500">{companyInfo.address}</p>
                <p className="text-xs text-slate-500">{companyInfo.city}</p>
              </div>
            </div>
          </div>

          {/* Date Line */}
          <div className="flex justify-end mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Date:</span>
              <span className="font-mono text-sm border-b border-slate-400 px-4 py-1 bg-white min-w-[120px]">
                {formData.check_date || '___/___/______'}
              </span>
            </div>
          </div>

          {/* Pay To Line */}
          <div className="flex items-start gap-2 mb-3">
            <span className="text-sm text-slate-600 whitespace-nowrap">PAY TO THE ORDER OF:</span>
            <div className="flex-1 border-b-2 border-slate-400 px-2 py-1 bg-white min-h-[32px]">
              <span className="font-semibold text-slate-800">{formData.payee || '_______________________________'}</span>
            </div>
            <div className="border-2 border-slate-400 rounded px-4 py-1 bg-white font-mono font-bold text-lg min-w-[120px] text-center">
              ${formData.amount ? parseFloat(formData.amount).toLocaleString('en-US', {minimumFractionDigits: 2}) : '****.**'}
            </div>
          </div>

          {/* Amount in Words */}
          <div className="flex items-center gap-2 mb-4">
            <div className="flex-1 border-b-2 border-slate-400 px-2 py-1 bg-white min-h-[32px]">
              <span className="text-sm italic text-slate-700">
                {formData.amount ? amountToWords(formData.amount) : '________________________________'} DOLLARS
              </span>
            </div>
          </div>

          {/* Bank Info */}
          <div className="flex justify-between items-end mt-6 pt-4 border-t border-slate-200">
            <div className="text-xs text-slate-500">
              <p className="font-semibold">{companyInfo.bank}</p>
              <p>Routing: {companyInfo.routing} | Acct: {companyInfo.account}</p>
            </div>
            <div className="text-right">
              <span className="text-sm text-slate-500">Memo:</span>
              <span className="ml-2 font-mono text-sm border-b border-slate-300 px-2 py-0.5 inline-block min-w-[150px]">
                {formData.memo || ''}
              </span>
            </div>
          </div>

          {/* Signature Line */}
          <div className="flex justify-end mt-4">
            <div className="text-center">
              <div className="border-b-2 border-slate-400 w-48 mb-1"></div>
              <span className="text-xs text-slate-500">Authorized Signature</span>
            </div>
          </div>

          {/* MICR Line (decorative) */}
          <div className="mt-4 pt-2 border-t border-slate-200">
            <p className="font-mono text-xs text-slate-400 tracking-wider">
              ⑈{companyInfo.routing}⑈ {companyInfo.account}⑆ {nextCheckNumber}
            </p>
          </div>
        </div>
      </Card>

      {/* Check Entry Form */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Check Details</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Payee *</label>
                <Input 
                  placeholder="Enter vendor/payee name..." 
                  className="mt-1"
                  value={formData.payee}
                  onChange={(e) => setFormData({...formData, payee: e.target.value})}
                  required
                  data-testid="check-payee"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Amount *</label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input 
                    placeholder="0.00" 
                    type="number" 
                    step="0.01"
                    className="pl-7"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    required
                    data-testid="check-amount"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">GL Account *</label>
                <select 
                  className="mt-1 w-full rounded-md border border-gray-200 p-2 text-sm"
                  value={formData.gl_account}
                  onChange={(e) => setFormData({...formData, gl_account: e.target.value})}
                  data-testid="check-account"
                >
                  {accounts.map(acc => (
                    <option key={acc.number} value={acc.number}>{acc.number} - {acc.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Check Date</label>
                <Input 
                  type="date" 
                  className="mt-1"
                  value={formData.check_date}
                  onChange={(e) => setFormData({...formData, check_date: e.target.value})}
                  data-testid="check-date"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Memo</label>
              <Input 
                placeholder="Enter memo..." 
                className="mt-1"
                value={formData.memo}
                onChange={(e) => setFormData({...formData, memo: e.target.value})}
                data-testid="check-memo"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="submit" disabled={loading} data-testid="create-check-btn">
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Check
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* AI Auto-coding hint */}
      <Card className="bg-purple-50/50 border-purple-200">
        <CardContent className="p-3 flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-purple-500" />
          <p className="text-sm text-purple-700">AI auto-suggests GL account based on payee history</p>
        </CardContent>
      </Card>

      {/* Recent Checks */}
      {checks.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Pending Checks</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="p-3 text-left font-medium text-gray-600">Check #</th>
                  <th className="p-3 text-left font-medium text-gray-600">Payee</th>
                  <th className="p-3 text-left font-medium text-gray-600">Date</th>
                  <th className="p-3 text-right font-medium text-gray-600">Amount</th>
                  <th className="p-3 text-left font-medium text-gray-600">Status</th>
                  <th className="p-3 text-right font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {checks.map((check) => (
                  <tr key={check.check_id} className="hover:bg-slate-50">
                    <td className="p-3 font-mono">{check.check_number}</td>
                    <td className="p-3 font-medium">{check.payee}</td>
                    <td className="p-3 text-gray-500">{check.check_date}</td>
                    <td className="p-3 text-right font-medium">${check.amount.toLocaleString()}</td>
                    <td className="p-3">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        {check.status}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handlePrintCheck(check.check_id)}
                          title="Print Check PDF"
                        >
                          <FileText className="w-4 h-4 text-sky-500" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleVoid(check.check_id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </>
  );
};

// ==================== ACCOUNTS PAYABLE ====================
const AccountsPayable = () => {
  const { companyId } = useCompany();
  const [bills, setBills] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    vendor_name: '',
    invoice_number: '',
    amount: '',
    due_date: '',
    gl_account: '6000',
    description: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBills();
  }, []);

  const fetchBills = async () => {
    try {
      const res = await fetch(`${API_URL}/api/transactions/bills?company_id=${companyId || 'default'}`, { credentials: 'include' });
      const data = await res.json();
      setBills(data);
    } catch (err) {
      console.error('Error fetching bills:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/transactions/bills`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          vendor_id: `V-${Date.now()}`,
          amount: parseFloat(formData.amount),
          company_id: companyId || 'default'
        })
      });
      if (res.ok) {
        setShowForm(false);
        setFormData({ vendor_name: '', invoice_number: '', amount: '', due_date: '', gl_account: '6000', description: '' });
        fetchBills();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) {
      console.error('Error creating bill:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleHold = async (billId) => {
    await fetch(`${API_URL}/api/transactions/bills/${billId}/hold`, { method: 'POST', credentials: 'include' });
    fetchBills();
  };

  const handleRelease = async (billId) => {
    await fetch(`${API_URL}/api/transactions/bills/${billId}/release`, { method: 'POST', credentials: 'include' });
    fetchBills();
  };

  const handleApprove = async (billId) => {
    await fetch(`${API_URL}/api/transactions/bills/${billId}/approve`, { method: 'POST', credentials: 'include' });
    fetchBills();
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Accounts Payable</h1>
          <p className="text-gray-500 mt-1">Manage vendor invoices and payments</p>
        </div>
        <Button onClick={() => setShowForm(true)} data-testid="new-bill-btn">
          <Plus className="w-4 h-4 mr-2" />New Bill
        </Button>
      </div>

      {/* New Bill Form Modal */}
      {showForm && (
        <Card className="border-sky-200 bg-sky-50/30">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Enter New Bill</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Vendor Name *</label>
                  <Input 
                    value={formData.vendor_name}
                    onChange={(e) => setFormData({...formData, vendor_name: e.target.value})}
                    required
                    data-testid="bill-vendor"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Invoice # *</label>
                  <Input 
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({...formData, invoice_number: e.target.value})}
                    required
                    data-testid="bill-invoice"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Amount *</label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    required
                    data-testid="bill-amount"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Due Date *</label>
                  <Input 
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    required
                    data-testid="bill-due-date"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <Input 
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" disabled={loading} data-testid="save-bill-btn">
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Save Bill'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Bills List */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="p-3 text-left font-medium text-gray-600">Invoice</th>
                <th className="p-3 text-left font-medium text-gray-600">Vendor</th>
                <th className="p-3 text-right font-medium text-gray-600">Amount</th>
                <th className="p-3 text-left font-medium text-gray-600">Due Date</th>
                <th className="p-3 text-left font-medium text-gray-600">Status</th>
                <th className="p-3 text-right font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {bills.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-gray-500">
                    No bills found. Click "New Bill" to add one.
                  </td>
                </tr>
              ) : bills.map((bill) => (
                <tr key={bill.bill_id} className="hover:bg-slate-50">
                  <td className="p-3 font-mono text-sm">{bill.invoice_number}</td>
                  <td className="p-3 font-medium">{bill.vendor_name}</td>
                  <td className="p-3 text-right">${bill.amount?.toLocaleString()}</td>
                  <td className="p-3">{bill.due_date}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium
                      ${bill.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        bill.status === 'posted' ? 'bg-sky-100 text-sky-700' :
                        bill.on_hold ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}`}>
                      {bill.on_hold && <Pause className="w-3 h-3 inline mr-1" />}
                      {bill.on_hold ? 'On Hold' : bill.status}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      {bill.on_hold ? (
                        <Button variant="ghost" size="sm" onClick={() => handleRelease(bill.bill_id)} title="Release">
                          <Play className="w-4 h-4 text-emerald-500" />
                        </Button>
                      ) : bill.status === 'pending' && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => handleHold(bill.bill_id)} title="Hold">
                            <Pause className="w-4 h-4 text-amber-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleApprove(bill.bill_id)} title="Approve">
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* AI Features */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-purple-50/50 border-purple-200">
          <CardContent className="p-3 flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-purple-500" />
            <div>
              <p className="text-sm font-medium text-purple-800">Duplicate Detection</p>
              <p className="text-xs text-purple-600">AI scanning for duplicate vendors/invoices</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50/50 border-purple-200">
          <CardContent className="p-3 flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-purple-500" />
            <div>
              <p className="text-sm font-medium text-purple-800">Cash Forecast</p>
              <p className="text-xs text-purple-600">View predicted cash impact</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

// Manual Check (redirect to Quick Check)
const ManualCheck = () => <QuickCheck />;

// ==================== DEPOSITS ====================
const Deposits = () => {
  const { companyId } = useCompany();
  const [deposits, setDeposits] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    bank_account: 'Operating Account',
    amount: '',
    source: '',
    reference: '',
    deposit_date: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDeposits();
  }, []);

  const fetchDeposits = async () => {
    try {
      const res = await fetch(`${API_URL}/api/transactions/deposits?company_id=${companyId || 'default'}`, { credentials: 'include' });
      const data = await res.json();
      setDeposits(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/transactions/deposits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...formData, amount: parseFloat(formData.amount), company_id: companyId || 'default' })
      });
      if (res.ok) {
        setShowForm(false);
        setFormData({ bank_account: 'Operating Account', amount: '', source: '', reference: '', deposit_date: new Date().toISOString().split('T')[0] });
        fetchDeposits();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Deposits</h1>
          <p className="text-gray-500 mt-1">Record bank deposits</p>
        </div>
        <Button onClick={() => setShowForm(true)} data-testid="new-deposit-btn">
          <Plus className="w-4 h-4 mr-2" />New Deposit
        </Button>
      </div>

      {showForm && (
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Record Deposit</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Bank Account</label>
                  <select 
                    className="mt-1 w-full rounded-md border border-gray-200 p-2"
                    value={formData.bank_account}
                    onChange={(e) => setFormData({...formData, bank_account: e.target.value})}
                  >
                    <option>Operating Account</option>
                    <option>Savings Account</option>
                    <option>Payroll Account</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Amount *</label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    required
                    data-testid="deposit-amount"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Source *</label>
                  <Input 
                    placeholder="Customer payment, cash sales..."
                    value={formData.source}
                    onChange={(e) => setFormData({...formData, source: e.target.value})}
                    required
                    data-testid="deposit-source"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Date</label>
                  <Input 
                    type="date"
                    value={formData.deposit_date}
                    onChange={(e) => setFormData({...formData, deposit_date: e.target.value})}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" disabled={loading} data-testid="save-deposit-btn">
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Save Deposit'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="p-3 text-left font-medium text-gray-600">ID</th>
                <th className="p-3 text-left font-medium text-gray-600">Account</th>
                <th className="p-3 text-left font-medium text-gray-600">Source</th>
                <th className="p-3 text-left font-medium text-gray-600">Date</th>
                <th className="p-3 text-right font-medium text-gray-600">Amount</th>
                <th className="p-3 text-left font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {deposits.length === 0 ? (
                <tr><td colSpan="6" className="p-8 text-center text-gray-500">No deposits recorded yet.</td></tr>
              ) : deposits.map((dep) => (
                <tr key={dep.deposit_id} className="hover:bg-slate-50">
                  <td className="p-3 font-mono text-sm">{dep.deposit_id}</td>
                  <td className="p-3">{dep.bank_account}</td>
                  <td className="p-3">{dep.source}</td>
                  <td className="p-3">{dep.deposit_date}</td>
                  <td className="p-3 text-right font-medium text-emerald-600">${dep.amount?.toLocaleString()}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${dep.status === 'posted' ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'}`}>
                      {dep.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </>
  );
};

// Accounts Receivable - Full Invoice Management
const AccountsReceivable = () => {
  const { companyId } = useCompany();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    payment_method: 'check',
    reference_number: '',
    payment_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [formData, setFormData] = useState({
    customer_id: '',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    tax_rate: 0,
    notes: '',
    line_items: [{ description: '', quantity: 1, unit: '', unit_price: 0, gl_account: '', enterprise: '', operation: '', equipment: '' }]
  });

  useEffect(() => {
    fetchInvoices();
    fetchCustomers();
  }, []);

  const fetchInvoices = async () => {
    try {
      const res = await fetch(`${API_URL}/api/invoices?company_id=${companyId || 'default'}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setInvoices(data);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/customers`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCustomers(data);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleCreateInvoice = async () => {
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (formData.line_items.length === 0 || !formData.line_items[0].description) {
      toast.error('Please add at least one line item');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          customer_id: formData.customer_id,
          issue_date: formData.issue_date || null,
          due_date: formData.due_date || null,
          tax_rate: parseFloat(formData.tax_rate) || 0,
          notes: formData.notes,
          line_items: formData.line_items.filter(item => item.description).map(({ unit, gl_account, enterprise, operation, equipment, ...rest }) => ({
            ...rest,
            quantity: parseFloat(rest.quantity) || 0,
            unit_price: parseFloat(rest.unit_price) || 0,
            unit: unit || null, gl_account: gl_account || null, enterprise: enterprise || null, operation: operation || null, equipment: equipment || null,
          })),
          company_id: companyId || 'default'
        })
      });

      if (res.ok) {
        toast.success('Invoice created successfully');
        setShowCreateModal(false);
        resetForm();
        fetchInvoices();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create invoice');
      }
    } catch (err) {
      toast.error('Failed to create invoice');
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/${invoiceId}/send`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success('Invoice marked as sent');
        fetchInvoices();
      } else {
        toast.error('Failed to send invoice');
      }
    } catch (err) {
      toast.error('Failed to send invoice');
    }
  };

  // A draft can be deleted (it is kept so it can be restored). A sent invoice is voided instead, with a reason, so a record stays on file.
  const handleRemoveInvoice = async (invoice) => {
    const isDraft = invoice.status === 'draft';
    let url = `${API_URL}/api/invoices/${invoice.id}`;
    const init = { credentials: 'include' };
    if (isDraft) {
      if (!window.confirm('Delete this draft invoice? It can be restored by an administrator, manager or accountant.')) return;
      init.method = 'DELETE';
    } else {
      const reason = window.prompt(`Void invoice ${invoice.invoice_number || ''}? It stays on file as cancelled.\n\nWhy is it being voided? (saved in the audit trail)`);
      if (!reason) return;
      url += '/void';
      init.method = 'POST';
      init.headers = { 'Content-Type': 'application/json' };
      init.body = JSON.stringify({ reason });
    }
    try {
      const res = await fetch(url, init);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(isDraft ? 'Invoice deleted' : 'Invoice voided');
        fetchInvoices();
      } else {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Could not remove the invoice');
      }
    } catch (err) {
      toast.error('Could not remove the invoice');
    }
  };

  const openPaymentModal = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentData({
      amount: invoice.balance_due || 0,
      payment_method: 'check',
      reference_number: '',
      payment_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setShowPaymentModal(true);
  };

  const handleRecordPayment = async () => {
    if (!paymentData.amount || paymentData.amount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    if (paymentData.amount > selectedInvoice.balance_due) {
      toast.error(`Payment cannot exceed balance due ($${selectedInvoice.balance_due})`);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/invoices/${selectedInvoice.id}/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          invoice_id: selectedInvoice.id,
          amount: parseFloat(paymentData.amount),
          payment_method: paymentData.payment_method,
          reference_number: paymentData.reference_number || null,
          payment_date: paymentData.payment_date,
          notes: paymentData.notes || null
        })
      });

      if (res.ok) {
        const result = await res.json();
        toast.success(`Payment of $${paymentData.amount.toLocaleString()} recorded. New balance: $${result.new_balance_due.toLocaleString()}`);
        setShowPaymentModal(false);
        setSelectedInvoice(null);
        fetchInvoices();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to record payment');
      }
    } catch (err) {
      toast.error('Failed to record payment');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      issue_date: new Date().toISOString().split('T')[0],
      due_date: '',
      tax_rate: 0,
      notes: '',
      line_items: [{ description: '', quantity: 1, unit: '', unit_price: 0, gl_account: '', enterprise: '', operation: '', equipment: '' }]
    });
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity: 1, unit: '', unit_price: 0, gl_account: '', enterprise: '', operation: '', equipment: '' }]
    }));
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length > 1) {
      setFormData(prev => ({
        ...prev,
        line_items: prev.line_items.filter((_, i) => i !== index)
      }));
    }
  };

  const updateLineItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const calculateSubtotal = () => {
    return formData.line_items.reduce((sum, item) => 
      sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0), 0
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-slate-100 text-slate-700',
      sent: 'bg-sky-100 text-sky-700',
      viewed: 'bg-purple-100 text-purple-700',
      partial: 'bg-amber-100 text-amber-700',
      paid: 'bg-emerald-100 text-emerald-700',
      overdue: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-500'
    };
    return colors[status] || 'bg-gray-100 text-gray-600';
  };

  const filteredInvoices = invoices.filter(inv => {
    const matchesFilter = filter === 'all' || inv.status === filter;
    const matchesSearch = !searchTerm || 
      inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const stats = {
    total: invoices.reduce((sum, inv) => sum + (inv.total || 0), 0),
    outstanding: invoices.filter(inv => ['sent', 'viewed', 'partial', 'overdue'].includes(inv.status))
      .reduce((sum, inv) => sum + (inv.balance_due || 0), 0),
    overdue: invoices.filter(inv => inv.status === 'overdue').length
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Accounts Receivable</h1>
          <p className="text-gray-500 mt-1">Manage customer invoices and payments</p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreateModal(true); }} data-testid="create-invoice-btn">
          <Plus className="w-4 h-4 mr-2" /> New Invoice
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Invoiced</p>
                <p className="text-2xl font-bold">${stats.total.toLocaleString()}</p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Outstanding</p>
                <p className="text-2xl font-bold text-amber-600">${stats.outstanding.toLocaleString()}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input 
            placeholder="Search invoices..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
          {['all', 'draft', 'sent', 'paid', 'overdue'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-3 py-1 rounded-md text-sm capitalize ${filter === status ? 'bg-white shadow-sm font-medium' : 'text-gray-600 hover:bg-white/50'}`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Invoice List */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading invoices...</div>
      ) : filteredInvoices.length === 0 ? (
        <Card className="text-center py-12">
          <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="font-medium text-gray-600">{searchTerm || filter !== 'all' ? 'No invoices found' : 'No invoices yet'}</h3>
          <p className="text-sm text-gray-500 mt-1">Create your first invoice to get started</p>
          {!searchTerm && filter === 'all' && (
            <Button className="mt-4" onClick={() => { resetForm(); setShowCreateModal(true); }}>
              <Plus className="w-4 h-4 mr-2" /> Create First Invoice
            </Button>
          )}
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-3 font-medium text-gray-600">Invoice #</th>
                  <th className="text-left p-3 font-medium text-gray-600">Customer</th>
                  <th className="text-left p-3 font-medium text-gray-600">Date</th>
                  <th className="text-left p-3 font-medium text-gray-600">Due Date</th>
                  <th className="text-right p-3 font-medium text-gray-600">Total</th>
                  <th className="text-right p-3 font-medium text-gray-600">Balance</th>
                  <th className="text-center p-3 font-medium text-gray-600">Status</th>
                  <th className="text-center p-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredInvoices.map(invoice => (
                  <tr key={invoice.id} className="hover:bg-gray-50" data-testid={`invoice-row-${invoice.id}`}>
                    <td className="p-3 font-mono text-sm text-sky-600">{invoice.invoice_number}</td>
                    <td className="p-3 font-medium">{invoice.customer_name}</td>
                    <td className="p-3 text-gray-600">{new Date(invoice.created_at).toLocaleDateString()}</td>
                    <td className="p-3 text-gray-600">{invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '-'}</td>
                    <td className="p-3 text-right font-mono">${(invoice.total || 0).toLocaleString()}</td>
                    <td className="p-3 text-right font-mono">${(invoice.balance_due || 0).toLocaleString()}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(invoice.status)}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex justify-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedInvoice(invoice); setShowViewModal(true); }} title="View">
                          <Eye className="w-4 h-4" />
                        </Button>
                        {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600" onClick={() => openPaymentModal(invoice)} title="Record Payment" data-testid={`record-payment-${invoice.id}`}>
                            <DollarSign className="w-4 h-4" />
                          </Button>
                        )}
                        {invoice.status === 'draft' && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-sky-600" onClick={() => handleSendInvoice(invoice.id)} title="Send">
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                        {invoice.status !== 'cancelled' && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleRemoveInvoice(invoice)}
                            title={invoice.status === 'draft' ? 'Delete draft' : 'Void invoice'} data-testid={`remove-invoice-${invoice.id}`}>
                            {invoice.status === 'draft' ? <Trash2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white z-10 border-b">
              <CardTitle>Create New Invoice</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowCreateModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>Customer *</Label>
                  <select 
                    className="w-full border rounded-md p-2"
                    value={formData.customer_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, customer_id: e.target.value }))}
                    data-testid="invoice-customer-select"
                  >
                    <option value="">Select Customer...</option>
                    {[...customers].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })).map(c => (
                      <option key={c.id} value={c.id}>{c.name}{c.is_cod ? ' (COD)' : ''}</option>
                    ))}
                  </select>
                  {customers.find(c => c.id === formData.customer_id)?.is_cod && (
                    <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="cod-warning">
                      This customer pays cash on delivery (COD). Collect payment when the goods are delivered.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Invoice Date</Label>
                  <Input type="date" value={formData.issue_date} onChange={(e) => setFormData(prev => ({ ...prev, issue_date: e.target.value }))} data-testid="invoice-date" />
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input 
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tax Rate (%)</Label>
                  <Input 
                    type="number"
                    step="0.1"
                    value={formData.tax_rate}
                    onChange={(e) => setFormData(prev => ({ ...prev, tax_rate: e.target.value }))}
                  />
                </div>
              </div>

              {/* Line Items */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Line Items</Label>
                  <Button variant="outline" size="sm" onClick={addLineItem}>
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.line_items.map((item, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <Input 
                        placeholder="Description"
                        className="flex-1"
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        data-testid={`line-item-desc-${index}`}
                      />
                      <Input 
                        type="number"
                        placeholder="Qty"
                        className="w-20"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                      />
                      <Input 
                        placeholder="Unit"
                        title="Unit of measure (each, dozen, ton, crate...)"
                        className="w-24"
                        value={item.unit || ''}
                        onChange={(e) => updateLineItem(index, 'unit', e.target.value)}
                        data-testid={`line-item-unit-${index}`}
                      />
                      <Input 
                        type="number"
                        placeholder="Price"
                        className="w-28"
                        value={item.unit_price}
                        onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                      />
                      <span className="w-24 text-right font-mono py-2">
                        ${((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)).toLocaleString()}
                      </span>
                      {formData.line_items.length > 1 && (
                        <Button variant="ghost" size="icon" className="text-red-500" onClick={() => removeLineItem(index)}>
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <p className="text-xs text-gray-500">Optional on each line: charge the revenue to an account, enterprise, operation and equipment.</p>
                  {formData.line_items.map((item, index) => (
                    <div key={`dims-${index}`} className="grid grid-cols-4 gap-2" data-testid={`line-item-dims-${index}`}>
                      {[['gl_account', 'GL account'], ['enterprise', 'Enterprise'], ['operation', 'Operation'], ['equipment', 'Equipment']].map(([field, label]) => (
                        <Input key={field} placeholder={`${label} (line ${index + 1})`} value={item[field] || ''} onChange={(e) => updateLineItem(index, field, e.target.value)} />
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-mono">${calculateSubtotal().toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax ({formData.tax_rate}%)</span>
                  <span className="font-mono">${(calculateSubtotal() * (parseFloat(formData.tax_rate) || 0) / 100).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="font-mono">${(calculateSubtotal() * (1 + (parseFloat(formData.tax_rate) || 0) / 100)).toLocaleString()}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <textarea 
                  className="w-full border rounded-md p-2 min-h-[60px]"
                  placeholder="Additional notes for the customer..."
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                <Button onClick={handleCreateInvoice} data-testid="save-invoice-btn">Create Invoice</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* View Invoice Modal */}
      {showViewModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl">
            <CardHeader className="flex flex-row items-center justify-between border-b">
              <div>
                <CardTitle>Invoice {selectedInvoice.invoice_number}</CardTitle>
                <p className="text-sm text-gray-500">{selectedInvoice.customer_name}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => { setShowViewModal(false); setSelectedInvoice(null); }}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Status</p>
                  <span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(selectedInvoice.status)}`}>
                    {selectedInvoice.status}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Invoice Date</p>
                  <p className="font-medium">{new Date(selectedInvoice.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Due Date</p>
                  <p className="font-medium">{selectedInvoice.due_date ? new Date(selectedInvoice.due_date).toLocaleDateString() : '-'}</p>
                </div>
              </div>

              {/* Line Items */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-2 text-sm font-medium text-gray-600">Description</th>
                      <th className="text-right p-2 text-sm font-medium text-gray-600">Qty</th>
                      <th className="text-right p-2 text-sm font-medium text-gray-600">Price</th>
                      <th className="text-right p-2 text-sm font-medium text-gray-600">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(selectedInvoice.line_items || []).map((item, index) => (
                      <tr key={index}>
                        <td className="p-2">{item.description}</td>
                        <td className="p-2 text-right">{item.quantity}</td>
                        <td className="p-2 text-right font-mono">${(item.unit_price || 0).toLocaleString()}</td>
                        <td className="p-2 text-right font-mono">${(item.amount || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-mono">${(selectedInvoice.subtotal || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax ({selectedInvoice.tax_rate}%)</span>
                  <span className="font-mono">${(selectedInvoice.tax_amount || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="font-mono">${(selectedInvoice.total || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-amber-600">
                  <span>Balance Due</span>
                  <span className="font-mono">${(selectedInvoice.balance_due || 0).toLocaleString()}</span>
                </div>
              </div>

              {selectedInvoice.notes && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase mb-1">Notes</p>
                  <p className="text-sm">{selectedInvoice.notes}</p>
                </div>
              )}

              <div className="flex gap-2 justify-end pt-4 border-t">
                {selectedInvoice.status !== 'paid' && selectedInvoice.status !== 'cancelled' && (
                  <Button variant="outline" className="text-emerald-600 border-emerald-300" onClick={() => { setShowViewModal(false); openPaymentModal(selectedInvoice); }}>
                    <DollarSign className="w-4 h-4 mr-2" /> Record Payment
                  </Button>
                )}
                {selectedInvoice.status === 'draft' && (
                  <Button variant="outline" onClick={() => { handleSendInvoice(selectedInvoice.id); setShowViewModal(false); }}>
                    <Send className="w-4 h-4 mr-2" /> Send Invoice
                  </Button>
                )}
                <Button onClick={() => { setShowViewModal(false); setSelectedInvoice(null); }}>Close</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Record Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between border-b">
              <div>
                <CardTitle>Record Payment</CardTitle>
                <p className="text-sm text-gray-500">{selectedInvoice.invoice_number} - {selectedInvoice.customer_name}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => { setShowPaymentModal(false); setSelectedInvoice(null); }}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              {/* Invoice Summary */}
              <div className="p-3 bg-slate-50 rounded-lg space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Invoice Total</span>
                  <span className="font-mono">${(selectedInvoice.total || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Already Paid</span>
                  <span className="font-mono text-emerald-600">${(selectedInvoice.amount_paid || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-bold border-t pt-1">
                  <span>Balance Due</span>
                  <span className="font-mono text-amber-600">${(selectedInvoice.balance_due || 0).toLocaleString()}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>Payment Amount *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <Input 
                      type="number"
                      step="0.01"
                      className="pl-8"
                      value={paymentData.amount}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                      data-testid="payment-amount-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button 
                      className="text-xs text-sky-600 hover:underline"
                      onClick={() => setPaymentData(prev => ({ ...prev, amount: selectedInvoice.balance_due }))}
                    >
                      Pay Full Balance
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <select 
                    className="w-full border rounded-md p-2"
                    value={paymentData.payment_method}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, payment_method: e.target.value }))}
                  >
                    <option value="check">Check</option>
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="credit_card">Credit Card</option>
                    <option value="ach">ACH</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Payment Date</Label>
                  <Input 
                    type="date"
                    value={paymentData.payment_date}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, payment_date: e.target.value }))}
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Reference # (Check #, Transaction ID)</Label>
                  <Input 
                    placeholder="e.g., Check #1234"
                    value={paymentData.reference_number}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, reference_number: e.target.value }))}
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Notes</Label>
                  <textarea 
                    className="w-full border rounded-md p-2 min-h-[60px]"
                    placeholder="Optional payment notes..."
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button variant="outline" onClick={() => { setShowPaymentModal(false); setSelectedInvoice(null); }}>Cancel</Button>
                <Button onClick={handleRecordPayment} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-payment-btn">
                  <DollarSign className="w-4 h-4 mr-2" /> Record Payment
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

// Paychecks - Full functionality
const Paychecks = () => {
  const navigate = useNavigate();
  const [paychecks, setPaychecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRunModal, setShowRunModal] = useState(false);
  const [runLoading, setRunLoading] = useState(false);
  const [payrollData, setPayrollData] = useState({
    pay_period_start: '',
    pay_period_end: '',
    pay_date: new Date().toISOString().split('T')[0]
  });
  const [lastRun, setLastRun] = useState(null);

  useEffect(() => {
    fetchPaychecks();
    // Set default pay period (last 2 weeks)
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 14);
    setPayrollData(prev => ({
      ...prev,
      pay_period_start: start.toISOString().split('T')[0],
      pay_period_end: end.toISOString().split('T')[0]
    }));
  }, []);

  const fetchPaychecks = async () => {
    try {
      const res = await fetch(`${API_URL}/api/payroll-full/paychecks`);
      if (res.ok) {
        const data = await res.json();
        setPaychecks(data);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const runPayroll = async () => {
    setRunLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payroll-full/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payrollData)
      });
      
      if (res.ok) {
        const data = await res.json();
        setLastRun(data);
        setShowRunModal(false);
        fetchPaychecks();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to run payroll');
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setRunLoading(false);
    }
  };

  const downloadPayslip = async (paycheckId) => {
    try {
      const res = await fetch(`${API_URL}/api/payroll-full/paychecks/${paycheckId}/payslip`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Payslip_${paycheckId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Paychecks</h1>
          <p className="text-gray-500 mt-1">Process employee paychecks with tax calculations</p>
        </div>
        <Button onClick={() => setShowRunModal(true)} data-testid="run-payroll-btn">
          <Play className="w-4 h-4 mr-2" />Run Payroll
        </Button>
      </div>

      {lastRun && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
                <div>
                  <h3 className="font-semibold text-emerald-800">Payroll Run Complete</h3>
                  <p className="text-sm text-emerald-600">
                    {lastRun.employee_count} employees • Total Gross: ${lastRun.total_gross?.toLocaleString()} • Total Net: ${lastRun.total_net?.toLocaleString()}
                  </p>
                </div>
              </div>
              <Button variant="outline" onClick={() => setLastRun(null)}>Dismiss</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading paychecks...</div>
      ) : paychecks.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No paychecks yet</p>
            <p className="text-sm mt-1">Add employees in Setup, then run payroll</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="p-3 text-left font-medium text-gray-600">Check ID</th>
                  <th className="p-3 text-left font-medium text-gray-600">Employee</th>
                  <th className="p-3 text-left font-medium text-gray-600">Pay Period</th>
                  <th className="p-3 text-right font-medium text-gray-600">Gross</th>
                  <th className="p-3 text-right font-medium text-gray-600">Taxes</th>
                  <th className="p-3 text-right font-medium text-gray-600">Net Pay</th>
                  <th className="p-3 text-center font-medium text-gray-600">Status</th>
                  <th className="p-3 text-right font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {paychecks.map((pc) => (
                  <tr key={pc.paycheck_id} className="hover:bg-slate-50">
                    <td className="p-3 font-mono text-sm">{pc.paycheck_id?.slice(-12)}</td>
                    <td className="p-3 font-medium">{pc.employee_name}</td>
                    <td className="p-3 text-sm text-gray-500">{pc.pay_period_start} - {pc.pay_period_end}</td>
                    <td className="p-3 text-right">${pc.gross_pay?.toLocaleString()}</td>
                    <td className="p-3 text-right text-red-600">${pc.taxes?.total?.toLocaleString()}</td>
                    <td className="p-3 text-right font-medium text-emerald-600">${pc.net_pay?.toLocaleString()}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        pc.status === 'voided' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {pc.status}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => downloadPayslip(pc.paycheck_id)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Run Payroll Modal */}
      {showRunModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Run Payroll</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowRunModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Pay Period Start</label>
                  <Input 
                    type="date"
                    value={payrollData.pay_period_start}
                    onChange={(e) => setPayrollData({...payrollData, pay_period_start: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Pay Period End</label>
                  <Input 
                    type="date"
                    value={payrollData.pay_period_end}
                    onChange={(e) => setPayrollData({...payrollData, pay_period_end: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Pay Date</label>
                <Input 
                  type="date"
                  value={payrollData.pay_date}
                  onChange={(e) => setPayrollData({...payrollData, pay_date: e.target.value})}
                />
              </div>
              <div className="p-3 bg-sky-50 rounded-lg text-sm text-sky-700">
                All active employees with approved timecards will be included in this payroll run.
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowRunModal(false)}>Cancel</Button>
                <Button onClick={runPayroll} disabled={runLoading}>
                  {runLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                  Run Payroll
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

// Timecards - Full functionality
const Timecards = () => {
  const [timecards, setTimecards] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [newTimecard, setNewTimecard] = useState({
    employee_id: '',
    pay_period_start: '',
    pay_period_end: '',
    entries: [],
    notes: ''
  });

  useEffect(() => {
    fetchTimecards();
    fetchEmployees();
    // Set default pay period
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 14);
    setNewTimecard(prev => ({
      ...prev,
      pay_period_start: start.toISOString().split('T')[0],
      pay_period_end: end.toISOString().split('T')[0]
    }));
  }, []);

  const fetchTimecards = async () => {
    try {
      const res = await fetch(`${API_URL}/api/payroll-full/timecards`);
      if (res.ok) {
        const data = await res.json();
        setTimecards(data);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${API_URL}/api/payroll-full/employees`);
      if (res.ok) {
        const data = await res.json();
        setEmployees(data);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const generateEntries = () => {
    // Generate daily entries for the pay period
    const entries = [];
    const start = new Date(newTimecard.pay_period_start);
    const end = new Date(newTimecard.pay_period_end);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dayOfWeek = d.getDay();
      entries.push({
        date: d.toISOString().split('T')[0],
        regular_hours: dayOfWeek === 0 || dayOfWeek === 6 ? 0 : 8, // No weekend hours by default
        overtime_hours: 0,
        sick_hours: 0,
        vacation_hours: 0
      });
    }
    setNewTimecard(prev => ({ ...prev, entries }));
  };

  const updateEntry = (index, field, value) => {
    const updated = [...newTimecard.entries];
    updated[index][field] = parseFloat(value) || 0;
    setNewTimecard(prev => ({ ...prev, entries: updated }));
  };

  const createTimecard = async () => {
    if (!newTimecard.employee_id) {
      alert('Please select an employee');
      return;
    }
    
    setFormLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payroll-full/timecards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newTimecard,
          status: 'draft'
        })
      });
      
      if (res.ok) {
        setShowForm(false);
        setNewTimecard({
          employee_id: '',
          pay_period_start: newTimecard.pay_period_start,
          pay_period_end: newTimecard.pay_period_end,
          entries: [],
          notes: ''
        });
        fetchTimecards();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const submitTimecard = async (timecardId) => {
    try {
      await fetch(`${API_URL}/api/payroll-full/timecards/${timecardId}/submit`, {
        method: 'POST'
      });
      fetchTimecards();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const approveTimecard = async (timecardId) => {
    try {
      await fetch(`${API_URL}/api/payroll-full/timecards/${timecardId}/approve`, {
        method: 'POST'
      });
      fetchTimecards();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const totalRegular = newTimecard.entries.reduce((sum, e) => sum + (e.regular_hours || 0), 0);
  const totalOvertime = newTimecard.entries.reduce((sum, e) => sum + (e.overtime_hours || 0), 0);

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Timecards</h1>
          <p className="text-gray-500 mt-1">Manage employee time entries for payroll</p>
        </div>
        <Button onClick={() => { setShowForm(true); generateEntries(); }} data-testid="new-timecard-btn">
          <Plus className="w-4 h-4 mr-2" />New Timecard
        </Button>
      </div>

      {showForm && (
        <Card className="border-sky-200 bg-sky-50/30">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Create Timecard</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium">Employee *</label>
                <select 
                  className="mt-1 w-full rounded-md border border-gray-200 p-2"
                  value={newTimecard.employee_id}
                  onChange={(e) => setNewTimecard({...newTimecard, employee_id: e.target.value})}
                >
                  <option value="">Select employee...</option>
                  {employees.map(emp => (
                    <option key={emp.employee_id} value={emp.employee_id}>{emp.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Period Start</label>
                <Input 
                  type="date"
                  value={newTimecard.pay_period_start}
                  onChange={(e) => {
                    setNewTimecard({...newTimecard, pay_period_start: e.target.value});
                  }}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Period End</label>
                <Input 
                  type="date"
                  value={newTimecard.pay_period_end}
                  onChange={(e) => {
                    setNewTimecard({...newTimecard, pay_period_end: e.target.value});
                  }}
                />
              </div>
            </div>
            
            <Button variant="outline" size="sm" onClick={generateEntries}>
              <RefreshCw className="w-4 h-4 mr-2" />Regenerate Days
            </Button>

            {newTimecard.entries.length > 0 && (
              <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="p-2 text-left">Date</th>
                      <th className="p-2 text-center">Regular</th>
                      <th className="p-2 text-center">Overtime</th>
                      <th className="p-2 text-center">Sick</th>
                      <th className="p-2 text-center">Vacation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newTimecard.entries.map((entry, idx) => {
                      const date = new Date(entry.date);
                      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                      return (
                        <tr key={idx} className={`border-t ${isWeekend ? 'bg-gray-50' : ''}`}>
                          <td className="p-2 font-medium">
                            {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </td>
                          <td className="p-1">
                            <Input 
                              type="number" 
                              className="w-16 text-center h-8"
                              value={entry.regular_hours}
                              onChange={(e) => updateEntry(idx, 'regular_hours', e.target.value)}
                              step="0.5"
                            />
                          </td>
                          <td className="p-1">
                            <Input 
                              type="number" 
                              className="w-16 text-center h-8"
                              value={entry.overtime_hours}
                              onChange={(e) => updateEntry(idx, 'overtime_hours', e.target.value)}
                              step="0.5"
                            />
                          </td>
                          <td className="p-1">
                            <Input 
                              type="number" 
                              className="w-16 text-center h-8"
                              value={entry.sick_hours}
                              onChange={(e) => updateEntry(idx, 'sick_hours', e.target.value)}
                              step="0.5"
                            />
                          </td>
                          <td className="p-1">
                            <Input 
                              type="number" 
                              className="w-16 text-center h-8"
                              value={entry.vacation_hours}
                              onChange={(e) => updateEntry(idx, 'vacation_hours', e.target.value)}
                              step="0.5"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <span className="font-medium">Totals:</span> Regular: {totalRegular}hrs | Overtime: {totalOvertime}hrs
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button onClick={createTimecard} disabled={formLoading}>
                  {formLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create Timecard
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading timecards...</div>
      ) : timecards.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No timecards yet</p>
            <p className="text-sm mt-1">Create a timecard for employee time tracking</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="p-3 text-left font-medium text-gray-600">Timecard ID</th>
                  <th className="p-3 text-left font-medium text-gray-600">Employee</th>
                  <th className="p-3 text-left font-medium text-gray-600">Period</th>
                  <th className="p-3 text-right font-medium text-gray-600">Regular Hrs</th>
                  <th className="p-3 text-right font-medium text-gray-600">OT Hrs</th>
                  <th className="p-3 text-center font-medium text-gray-600">Status</th>
                  <th className="p-3 text-right font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {timecards.map((tc) => (
                  <tr key={tc.timecard_id} className="hover:bg-slate-50">
                    <td className="p-3 font-mono text-sm">{tc.timecard_id}</td>
                    <td className="p-3 font-medium">{employees.find(e => e.employee_id === tc.employee_id)?.name || tc.employee_id}</td>
                    <td className="p-3 text-sm">{tc.pay_period_start} - {tc.pay_period_end}</td>
                    <td className="p-3 text-right">{tc.total_regular_hours}</td>
                    <td className="p-3 text-right">{tc.total_overtime_hours}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        tc.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        tc.status === 'submitted' ? 'bg-sky-100 text-sky-700' :
                        tc.status === 'processed' ? 'bg-purple-100 text-purple-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {tc.status}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      {tc.status === 'draft' && (
                        <Button variant="ghost" size="sm" onClick={() => submitTimecard(tc.timecard_id)}>
                          Submit
                        </Button>
                      )}
                      {tc.status === 'submitted' && (
                        <Button variant="ghost" size="sm" onClick={() => approveTimecard(tc.timecard_id)}>
                          Approve
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </>
  );
};

// Void Checks - Full functionality
const VoidChecks = () => {
  const [voidedChecks, setVoidedChecks] = useState([]);
  const [paychecks, setPaychecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidLoading, setVoidLoading] = useState(false);

  useEffect(() => {
    fetchVoidedChecks();
    fetchPaychecks();
  }, []);

  const fetchVoidedChecks = async () => {
    try {
      const res = await fetch(`${API_URL}/api/payroll-full/void-checks`);
      if (res.ok) {
        const data = await res.json();
        setVoidedChecks(data);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaychecks = async () => {
    try {
      const res = await fetch(`${API_URL}/api/payroll-full/paychecks`);
      if (res.ok) {
        const data = await res.json();
        setPaychecks(data.filter(pc => pc.status !== 'voided'));
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const voidCheck = async () => {
    if (!voidReason.trim()) {
      alert('Please provide a reason for voiding');
      return;
    }
    
    setVoidLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payroll-full/void-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          check_id: selectedCheck.paycheck_id,
          reason: voidReason,
          void_date: new Date().toISOString().split('T')[0]
        })
      });
      
      if (res.ok) {
        setShowVoidModal(false);
        setSelectedCheck(null);
        setVoidReason('');
        fetchVoidedChecks();
        fetchPaychecks();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to void check');
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setVoidLoading(false);
    }
  };

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Void Checks</h1>
        <p className="text-gray-500 mt-1">Void issued paychecks and view void history</p>
      </div>

      {/* Active Paychecks that can be voided */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Active Paychecks</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {paychecks.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No active paychecks to void</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="p-3 text-left font-medium text-gray-600">Check ID</th>
                  <th className="p-3 text-left font-medium text-gray-600">Employee</th>
                  <th className="p-3 text-left font-medium text-gray-600">Pay Date</th>
                  <th className="p-3 text-right font-medium text-gray-600">Net Amount</th>
                  <th className="p-3 text-right font-medium text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {paychecks.map((pc) => (
                  <tr key={pc.paycheck_id} className="hover:bg-slate-50">
                    <td className="p-3 font-mono text-sm">{pc.paycheck_id?.slice(-12)}</td>
                    <td className="p-3 font-medium">{pc.employee_name}</td>
                    <td className="p-3">{pc.pay_date}</td>
                    <td className="p-3 text-right font-medium">${pc.net_pay?.toLocaleString()}</td>
                    <td className="p-3 text-right">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => { setSelectedCheck(pc); setShowVoidModal(true); }}
                      >
                        <XCircle className="w-4 h-4 mr-1" /> Void
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Void History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Void History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : voidedChecks.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No voided checks</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="p-3 text-left font-medium text-gray-600">Void ID</th>
                  <th className="p-3 text-left font-medium text-gray-600">Original Check</th>
                  <th className="p-3 text-left font-medium text-gray-600">Employee</th>
                  <th className="p-3 text-right font-medium text-gray-600">Amount</th>
                  <th className="p-3 text-left font-medium text-gray-600">Reason</th>
                  <th className="p-3 text-left font-medium text-gray-600">Void Date</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {voidedChecks.map((v) => (
                  <tr key={v.void_id} className="hover:bg-slate-50">
                    <td className="p-3 font-mono text-sm text-red-600">{v.void_id}</td>
                    <td className="p-3 font-mono text-sm">{v.check_id?.slice(-12)}</td>
                    <td className="p-3">{v.employee_name}</td>
                    <td className="p-3 text-right font-medium">${v.original_amount?.toLocaleString()}</td>
                    <td className="p-3 text-gray-600">{v.reason}</td>
                    <td className="p-3">{v.void_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Void Modal */}
      {showVoidModal && selectedCheck && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-red-600">Void Check</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowVoidModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="font-medium text-red-800">You are about to void:</p>
                <p className="text-sm text-red-700 mt-1">
                  Check: {selectedCheck.paycheck_id?.slice(-12)}<br />
                  Employee: {selectedCheck.employee_name}<br />
                  Amount: ${selectedCheck.net_pay?.toLocaleString()}
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium">Reason for Void *</label>
                <Input 
                  placeholder="Enter reason for voiding this check..."
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  data-testid="void-reason"
                />
              </div>

              <div className="p-3 bg-amber-50 text-amber-800 rounded-lg text-sm">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                This action cannot be undone. The check will be marked as voided.
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowVoidModal(false)}>Cancel</Button>
                <Button variant="destructive" onClick={voidCheck} disabled={voidLoading}>
                  {voidLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                  Void Check
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

// ==================== JOURNAL ENTRIES ====================
const JournalEntries = () => {
  const { companyId } = useCompany();
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    memo: '',
    lines: [
      { account: '1000', account_name: 'Cash', debit: '', credit: '' },
      { account: '4000', account_name: 'Sales Revenue', debit: '', credit: '' }
    ]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationResult, setValidationResult] = useState(null);

  useEffect(() => {
    fetchEntries();
  }, [companyId]);

  const fetchEntries = async () => {
    try {
      const params = companyId ? `?company_id=${companyId}` : '';
      const res = await fetch(`${API_URL}/api/transactions/journal-entries${params}`, { credentials: 'include' });
      const data = await res.json();
      setEntries(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const validateEntry = async () => {
    const entry = {
      date: formData.date,
      memo: formData.memo,
      lines: formData.lines.map(l => ({
        account_number: l.account,
        account_name: l.account_name,
        debit: parseFloat(l.debit) || 0,
        credit: parseFloat(l.credit) || 0
      })),
      source_module: 'manual',
      company_id: companyId
    };
    
    try {
      const res = await fetch(`${API_URL}/api/accounting-controls/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry)
      });
      if (res.ok) {
        const result = await res.json();
        setValidationResult(result);
        return result;
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) {
      console.error('Validation error:', err);
    }
    return null;
  };

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { account: '', account_name: '', debit: '', credit: '' }]
    });
    setValidationResult(null);
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    setFormData({ ...formData, lines: newLines });
    setValidationResult(null);
  };

  const removeLine = (index) => {
    if (formData.lines.length > 2) {
      const newLines = formData.lines.filter((_, i) => i !== index);
      setFormData({ ...formData, lines: newLines });
      setValidationResult(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validate first using accounting controls
    const validation = await validateEntry();
    if (!validation?.is_valid) {
      return; // Validation errors shown in UI
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/accounting-controls/post?user_id=user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: formData.date,
          memo: formData.memo,
          lines: formData.lines.map(l => ({
            account_number: l.account,
            account_name: l.account_name,
            debit: parseFloat(l.debit) || 0,
            credit: parseFloat(l.credit) || 0
          })),
          source_module: 'manual',
          company_id: companyId
        })
      });
      if (res.ok) {
        setShowForm(false);
        setFormData({
          date: new Date().toISOString().split('T')[0],
          memo: '',
          lines: [
            { account: '1000', account_name: 'Cash', debit: '', credit: '' },
            { account: '4000', account_name: 'Sales Revenue', debit: '', credit: '' }
          ]
        });
        setValidationResult(null);
        fetchEntries();
        toast.success('Journal entry posted successfully');
      } else {
        const errData = await res.json();
        setError(errData.detail?.message || 'Failed to post entry');
      }
    } catch (err) {
      setError('Failed to create journal entry');
    } finally {
      setLoading(false);
    }
  };

  const totalDebits = formData.lines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0);
  const totalCredits = formData.lines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0);

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Journal Entries</h1>
          <p className="text-gray-500 mt-1">Create manual journal entries</p>
        </div>
        <Button onClick={() => setShowForm(true)} data-testid="new-je-btn">
          <Plus className="w-4 h-4 mr-2" />New Entry
        </Button>
      </div>

      {showForm && (
        <Card className="border-purple-200 bg-purple-50/30">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>New Journal Entry</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
                <XCircle className="w-5 h-5" />{error}
              </div>
            )}
            
            {/* Validation Results */}
            {validationResult && !validationResult.is_valid && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="font-medium text-red-700 mb-2">Validation Errors</p>
                <ul className="list-disc list-inside text-sm text-red-600 space-y-1">
                  {validationResult.errors?.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}
            {validationResult && validationResult.warnings?.length > 0 && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="font-medium text-amber-700 mb-2">Validation Warnings</p>
                <ul className="list-disc list-inside text-sm text-amber-600 space-y-1">
                  {validationResult.warnings.map((warn, i) => <li key={i}>{warn}</li>)}
                </ul>
                {validationResult.is_valid && <p className="text-xs text-amber-600 mt-2">Entry can still be posted.</p>}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Date</label>
                  <Input 
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Memo</label>
                  <Input 
                    placeholder="Description..."
                    value={formData.memo}
                    onChange={(e) => setFormData({...formData, memo: e.target.value})}
                    data-testid="je-memo"
                  />
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="p-2 text-left text-sm font-medium">Account</th>
                      <th className="p-2 text-left text-sm font-medium">Name</th>
                      <th className="p-2 text-right text-sm font-medium">Debit</th>
                      <th className="p-2 text-right text-sm font-medium">Credit</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.lines.map((line, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">
                          <Input 
                            className="w-24"
                            value={line.account}
                            onChange={(e) => updateLine(idx, 'account', e.target.value)}
                            placeholder="1000"
                          />
                        </td>
                        <td className="p-2">
                          <Input 
                            value={line.account_name}
                            onChange={(e) => updateLine(idx, 'account_name', e.target.value)}
                            placeholder="Account name"
                          />
                        </td>
                        <td className="p-2">
                          <Input 
                            type="number"
                            step="0.01"
                            className="w-28 text-right"
                            value={line.debit}
                            onChange={(e) => updateLine(idx, 'debit', e.target.value)}
                            placeholder="0.00"
                          />
                        </td>
                        <td className="p-2">
                          <Input 
                            type="number"
                            step="0.01"
                            className="w-28 text-right"
                            value={line.credit}
                            onChange={(e) => updateLine(idx, 'credit', e.target.value)}
                            placeholder="0.00"
                          />
                        </td>
                        <td className="p-2">
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeLine(idx)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t bg-slate-50">
                      <td colSpan="2" className="p-2">
                        <Button type="button" variant="ghost" size="sm" onClick={addLine}>
                          <Plus className="w-4 h-4 mr-1" />Add Line
                        </Button>
                      </td>
                      <td className="p-2 text-right font-bold">${totalDebits.toFixed(2)}</td>
                      <td className="p-2 text-right font-bold">${totalCredits.toFixed(2)}</td>
                      <td className="p-2">
                        {Math.abs(totalDebits - totalCredits) < 0.01 ? 
                          <CheckCircle className="w-5 h-5 text-emerald-500" /> : 
                          <XCircle className="w-5 h-5 text-red-500" />
                        }
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="button" variant="outline" onClick={validateEntry}>
                  <Shield className="w-4 h-4 mr-1" />Validate
                </Button>
                <Button type="submit" disabled={loading || Math.abs(totalDebits - totalCredits) > 0.01} data-testid="save-je-btn">
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Post Entry'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="p-3 text-left font-medium text-gray-600">ID</th>
                <th className="p-3 text-left font-medium text-gray-600">Date</th>
                <th className="p-3 text-left font-medium text-gray-600">Memo</th>
                <th className="p-3 text-right font-medium text-gray-600">Amount</th>
                <th className="p-3 text-left font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {entries.length === 0 ? (
                <tr><td colSpan="5" className="p-8 text-center text-gray-500">No journal entries yet.</td></tr>
              ) : entries.map((je) => (
                <tr key={je.je_id} className="hover:bg-slate-50">
                  <td className="p-3 font-mono text-sm">{je.je_id}</td>
                  <td className="p-3">{je.date}</td>
                  <td className="p-3">{je.memo}</td>
                  <td className="p-3 text-right font-medium">${je.total_debits?.toLocaleString()}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      je.status === 'posted' ? 'bg-sky-100 text-sky-700' : 
                      je.status === 'reversed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {je.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </>
  );
};

// ==================== POSTING QUEUE ====================
const PostingQueue = () => {
  const [queue, setQueue] = useState({ checks: { items: [], count: 0, total: 0 }, bills: { items: [], count: 0, total: 0 }, deposits: { items: [], count: 0, total: 0 }, journal_entries: { items: [], count: 0, total: 0 } });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchQueue();
  }, []);

  const fetchQueue = async () => {
    try {
      const res = await fetch(`${API_URL}/api/transactions/posting-queue`, { credentials: 'include' });
      const data = await res.json();
      setQueue(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const postAll = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/transactions/posting-queue/post-all`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      setMessage({ type: 'success', text: `Posted ${data.posted.checks + data.posted.bills + data.posted.deposits + data.posted.journal_entries} transactions` });
      fetchQueue();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to post' });
    } finally {
      setLoading(false);
    }
  };

  const totalCount = queue.checks.count + queue.bills.count + queue.deposits.count + queue.journal_entries.count;

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Posting & Review Queue</h1>
          <p className="text-gray-500 mt-1">Review and post pending transactions to GL</p>
        </div>
        <Button onClick={postAll} disabled={loading || totalCount === 0} data-testid="post-all-btn">
          {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
          Post All ({totalCount})
        </Button>
      </div>

      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {[
          { key: 'checks', label: 'Checks', icon: FileCheck, color: 'sky' },
          { key: 'bills', label: 'Bills (AP)', icon: CreditCard, color: 'amber' },
          { key: 'deposits', label: 'Deposits', icon: Wallet, color: 'emerald' },
          { key: 'journal_entries', label: 'Journal Entries', icon: BookMarked, color: 'purple' }
        ].map(({ key, label, icon: Icon, color }) => (
          <Card key={key} className={queue[key].count > 0 ? `border-${color}-200` : ''}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${queue[key].count > 0 ? `bg-${color}-100` : 'bg-slate-100'}`}>
                  <Icon className={`w-6 h-6 ${queue[key].count > 0 ? `text-${color}-600` : 'text-slate-400'}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{label}</h3>
                  <p className="text-sm text-gray-500">{queue[key].count} items • ${queue[key].total?.toLocaleString()}</p>
                </div>
              </div>
              {queue[key].count > 0 && (
                <span className={`px-2 py-1 bg-${color}-100 text-${color}-700 rounded-full text-xs font-medium`}>
                  Ready
                </span>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* AI Validation */}
      <Card className="bg-purple-50/50 border-purple-200">
        <CardContent className="p-4 flex items-center gap-4">
          <Sparkles className="w-8 h-8 text-purple-500" />
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">AI Pre-Post Validation</h3>
            <p className="text-sm text-gray-600">Checking for missing codes, suspicious totals, and policy violations</p>
          </div>
          <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
            {totalCount > 0 ? `${totalCount} ready to post` : 'Queue empty'}
          </span>
        </CardContent>
      </Card>
    </>
  );
};

// ==================== CA FARM PAYROLL ====================
const CAFarmPayroll = () => {
  const { companyId, selectedCompany } = useCompany();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [employers, setEmployers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [paychecks, setPaychecks] = useState([]);
  const [payRegister, setPayRegister] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0, status: '' });
  
  // Form states
  const [employerForm, setEmployerForm] = useState({
    company_name: selectedCompany?.name || '',
    ein: '',
    state_ein: '',
    address: '',
    city: '',
    state: 'CA',
    zip: '',
    pay_frequency: 'weekly',
    farm_ein: '',
    h2a_registered: false,
    labor_contractor_license: ''
  });

  const [timeEntryForm, setTimeEntryForm] = useState({
    employee_id: '',
    date: new Date().toISOString().split('T')[0],
    regular_hours: 8,
    overtime_hours: 0,
    double_time_hours: 0,
    pieces: 0,
    piece_rate: 0,
    job_code: '',
    notes: ''
  });

  const steps = [
    { num: 1, title: 'Employer Setup', desc: 'Company & Tax Settings' },
    { num: 2, title: 'Time Entry', desc: 'Hours & Piece Work' },
    { num: 3, title: 'Calculate', desc: 'Tax & Progress' },
    { num: 4, title: 'Review', desc: 'Paychecks & Register' },
    { num: 5, title: 'Post', desc: 'Annual Records' }
  ];

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const fetchData = async () => {
    try {
      const params = companyId ? `?company_id=${companyId}` : '';
      const [empRes, empyRes, timeRes] = await Promise.all([
        fetch(`${API_URL}/api/ca-payroll/employers${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/ca-payroll/employees${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/ca-payroll/time-entries${params}`, { credentials: 'include' })
      ]);
      
      if (empRes.ok) setEmployers(await empRes.json());
      if (empyRes.ok) setEmployees(await empyRes.json());
      if (timeRes.ok) setTimeEntries(await timeRes.json());
    } catch (err) {
      console.error('Fetch error:', err);
    }
  };

  const handleEmployerSubmit = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/employers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...employerForm, company_id: companyId })
      });
      if (res.ok) {
        toast.success('Employer setup saved!');
        fetchData();
        setCurrentStep(2);
      } else {
        toast.error('Failed to save employer setup');
      }
    } catch (err) {
      toast.error('Error saving employer');
    } finally {
      setLoading(false);
    }
  };

  const handleTimeEntrySubmit = async () => {
    if (!timeEntryForm.employee_id) {
      toast.error('Please select an employee');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/time-entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...timeEntryForm, company_id: companyId })
      });
      if (res.ok) {
        toast.success('Time entry added!');
        fetchData();
        setTimeEntryForm(prev => ({ ...prev, date: new Date().toISOString().split('T')[0], regular_hours: 8, overtime_hours: 0, pieces: 0, notes: '' }));
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) {
      toast.error('Error adding time entry');
    } finally {
      setLoading(false);
    }
  };

  const runPayrollCalculation = async () => {
    setLoading(true);
    setProgress({ current: 0, total: employees.length, status: 'Starting...' });
    
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          company_id: companyId,
          pay_period_start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          pay_period_end: new Date().toISOString().split('T')[0],
          pay_date: new Date().toISOString().split('T')[0]
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setPaychecks(data.paychecks || []);
        setPayRegister(data.register || null);
        setProgress({ current: employees.length, total: employees.length, status: 'Complete!' });
        toast.success('Payroll calculated successfully!');
        setCurrentStep(4);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) {
      toast.error('Calculation failed');
    } finally {
      setLoading(false);
    }
  };

  const postToAnnualRecords = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/post-annual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ company_id: companyId, paychecks: paychecks.map(p => p.paycheck_id) })
      });
      if (res.ok) {
        toast.success('Posted to annual records!');
        setCurrentStep(1);
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch (err) {
      toast.error('Failed to post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Tractor className="w-7 h-7 text-emerald-600" />
            California Farm Payroll
          </h1>
          <p className="text-gray-500 mt-1">Agricultural payroll processing for California farms</p>
        </div>
      </div>

      {/* Progress Steps */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            {steps.map((step, idx) => (
              <div key={step.num} className="flex items-center">
                <button
                  onClick={() => setCurrentStep(step.num)}
                  className={`flex flex-col items-center ${currentStep === step.num ? 'text-emerald-600' : 'text-gray-400'}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                    currentStep > step.num ? 'bg-emerald-500' : 
                    currentStep === step.num ? 'bg-emerald-600' : 'bg-gray-300'
                  }`}>
                    {currentStep > step.num ? <CheckCircle className="w-5 h-5" /> : step.num}
                  </div>
                  <span className="mt-2 text-xs font-medium">{step.title}</span>
                  <span className="text-xs text-gray-400">{step.desc}</span>
                </button>
                {idx < steps.length - 1 && (
                  <div className={`w-20 h-0.5 mx-2 ${currentStep > step.num ? 'bg-emerald-500' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Step 1: Employer Setup */}
      {currentStep === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Employer / Company Setup
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company Name</Label>
                <Input 
                  value={employerForm.company_name}
                  onChange={(e) => setEmployerForm({...employerForm, company_name: e.target.value})}
                  data-testid="employer-name-input"
                />
              </div>
              <div>
                <Label>Federal EIN</Label>
                <Input 
                  placeholder="XX-XXXXXXX"
                  value={employerForm.ein}
                  onChange={(e) => setEmployerForm({...employerForm, ein: e.target.value})}
                />
              </div>
              <div>
                <Label>CA State EIN</Label>
                <Input 
                  value={employerForm.state_ein}
                  onChange={(e) => setEmployerForm({...employerForm, state_ein: e.target.value})}
                />
              </div>
              <div>
                <Label>Farm EIN (Schedule F)</Label>
                <Input 
                  value={employerForm.farm_ein}
                  onChange={(e) => setEmployerForm({...employerForm, farm_ein: e.target.value})}
                />
              </div>
              <div className="col-span-2">
                <Label>Address</Label>
                <Input 
                  value={employerForm.address}
                  onChange={(e) => setEmployerForm({...employerForm, address: e.target.value})}
                />
              </div>
              <div>
                <Label>City</Label>
                <Input 
                  value={employerForm.city}
                  onChange={(e) => setEmployerForm({...employerForm, city: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>State</Label>
                  <Input value="CA" disabled />
                </div>
                <div>
                  <Label>ZIP</Label>
                  <Input 
                    value={employerForm.zip}
                    onChange={(e) => setEmployerForm({...employerForm, zip: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <Label>Pay Frequency</Label>
                <select 
                  className="w-full border rounded-md p-2"
                  value={employerForm.pay_frequency}
                  onChange={(e) => setEmployerForm({...employerForm, pay_frequency: e.target.value})}
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-Weekly</option>
                  <option value="semimonthly">Semi-Monthly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div>
                <Label>Labor Contractor License #</Label>
                <Input 
                  value={employerForm.labor_contractor_license}
                  onChange={(e) => setEmployerForm({...employerForm, labor_contractor_license: e.target.value})}
                  placeholder="Optional"
                />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input 
                  type="checkbox"
                  checked={employerForm.h2a_registered}
                  onChange={(e) => setEmployerForm({...employerForm, h2a_registered: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label className="cursor-pointer">H-2A Visa Program Registered</Label>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button onClick={handleEmployerSubmit} disabled={loading} data-testid="save-employer-btn">
                {loading && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
                Save & Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Time Entry */}
      {currentStep === 2 && (
        <div className="grid grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Add Time Entry
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Employee</Label>
                <select 
                  className="w-full border rounded-md p-2"
                  value={timeEntryForm.employee_id}
                  onChange={(e) => setTimeEntryForm({...timeEntryForm, employee_id: e.target.value})}
                  data-testid="time-entry-employee-select"
                >
                  <option value="">Select Employee...</option>
                  {employees.map(emp => (
                    <option key={emp.employee_id} value={emp.employee_id}>{emp.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Date</Label>
                <Input 
                  type="date"
                  value={timeEntryForm.date}
                  onChange={(e) => setTimeEntryForm({...timeEntryForm, date: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label>Regular Hrs</Label>
                  <Input 
                    type="number"
                    value={timeEntryForm.regular_hours}
                    onChange={(e) => setTimeEntryForm({...timeEntryForm, regular_hours: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>OT Hrs (1.5x)</Label>
                  <Input 
                    type="number"
                    value={timeEntryForm.overtime_hours}
                    onChange={(e) => setTimeEntryForm({...timeEntryForm, overtime_hours: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>DT Hrs (2x)</Label>
                  <Input 
                    type="number"
                    value={timeEntryForm.double_time_hours}
                    onChange={(e) => setTimeEntryForm({...timeEntryForm, double_time_hours: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>

              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-sm font-medium text-amber-800">Piece Work (Optional)</p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <Label className="text-xs">Pieces/Units</Label>
                    <Input 
                      type="number"
                      value={timeEntryForm.pieces}
                      onChange={(e) => setTimeEntryForm({...timeEntryForm, pieces: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Rate per Piece</Label>
                    <Input 
                      type="number"
                      step="0.01"
                      value={timeEntryForm.piece_rate}
                      onChange={(e) => setTimeEntryForm({...timeEntryForm, piece_rate: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label>Job Code</Label>
                <Input 
                  placeholder="e.g., HARVEST, PRUNE, PACK"
                  value={timeEntryForm.job_code}
                  onChange={(e) => setTimeEntryForm({...timeEntryForm, job_code: e.target.value})}
                />
              </div>

              <div>
                <Label>Notes</Label>
                <textarea 
                  className="w-full border rounded-md p-2 min-h-[60px]"
                  value={timeEntryForm.notes}
                  onChange={(e) => setTimeEntryForm({...timeEntryForm, notes: e.target.value})}
                />
              </div>

              <Button onClick={handleTimeEntrySubmit} disabled={loading} className="w-full" data-testid="add-time-entry-btn">
                {loading && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
                <Plus className="w-4 h-4 mr-2" />
                Add Entry
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent Time Entries</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {timeEntries.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No time entries yet</p>
                ) : (
                  timeEntries.map((entry, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-lg flex justify-between items-center">
                      <div>
                        <p className="font-medium">{employees.find(e => e.employee_id === entry.employee_id)?.name || entry.employee_id}</p>
                        <p className="text-xs text-gray-500">{entry.date} • {entry.job_code || 'General'}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono">{entry.regular_hours}h + {entry.overtime_hours}h OT</p>
                        {entry.pieces > 0 && <p className="text-xs text-emerald-600">{entry.pieces} pieces</p>}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t mt-4">
                <Button variant="outline" onClick={() => setCurrentStep(1)}>Back</Button>
                <Button onClick={() => setCurrentStep(3)} data-testid="proceed-to-calc-btn">
                  Continue to Calculate
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 3: Calculate */}
      {currentStep === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="w-5 h-5" />
              Calculate Payroll
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 bg-slate-50 rounded-lg">
              <h3 className="font-semibold mb-2">Payroll Summary</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-sky-600">{employees.length}</p>
                  <p className="text-sm text-gray-500">Employees</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{timeEntries.length}</p>
                  <p className="text-sm text-gray-500">Time Entries</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">
                    {timeEntries.reduce((sum, e) => sum + (e.regular_hours || 0) + (e.overtime_hours || 0), 0).toFixed(1)}h
                  </p>
                  <p className="text-sm text-gray-500">Total Hours</p>
                </div>
              </div>
            </div>

            {progress.total > 0 && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{progress.status}</span>
                  <span>{progress.current} / {progress.total}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-emerald-500 h-3 rounded-full transition-all"
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}

            <div className="p-3 bg-sky-50 rounded-lg text-sm text-sky-700">
              <p className="font-medium">California Tax Calculations Include:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Federal Income Tax (IRS Tables)</li>
                <li>Social Security (6.2%) & Medicare (1.45%)</li>
                <li>CA State Income Tax (Bracket System)</li>
                <li>CA SDI (State Disability Insurance - 1.1%)</li>
                <li>CA SUI/ETT (Employer Taxes)</li>
              </ul>
            </div>

            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => setCurrentStep(2)}>Back</Button>
              <Button onClick={runPayrollCalculation} disabled={loading || employees.length === 0} data-testid="run-calc-btn">
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                Run Calculation
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Review */}
      {currentStep === 4 && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Paycheck Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              {paychecks.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No paychecks generated yet. Run calculation first.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="p-3 text-left">Employee</th>
                        <th className="p-3 text-right">Gross</th>
                        <th className="p-3 text-right">Fed Tax</th>
                        <th className="p-3 text-right">State Tax</th>
                        <th className="p-3 text-right">FICA</th>
                        <th className="p-3 text-right">SDI</th>
                        <th className="p-3 text-right font-bold">Net Pay</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {paychecks.map((pc, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="p-3 font-medium">{pc.employee_name}</td>
                          <td className="p-3 text-right font-mono">${(pc.gross_pay || 0).toLocaleString()}</td>
                          <td className="p-3 text-right font-mono text-red-600">-${(pc.federal_tax || 0).toLocaleString()}</td>
                          <td className="p-3 text-right font-mono text-red-600">-${(pc.state_tax || 0).toLocaleString()}</td>
                          <td className="p-3 text-right font-mono text-red-600">-${(pc.fica || 0).toLocaleString()}</td>
                          <td className="p-3 text-right font-mono text-red-600">-${(pc.sdi || 0).toLocaleString()}</td>
                          <td className="p-3 text-right font-mono font-bold text-emerald-600">${(pc.net_pay || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-100 font-bold">
                      <tr>
                        <td className="p-3">TOTALS</td>
                        <td className="p-3 text-right font-mono">${paychecks.reduce((s, p) => s + (p.gross_pay || 0), 0).toLocaleString()}</td>
                        <td className="p-3 text-right font-mono text-red-600">-${paychecks.reduce((s, p) => s + (p.federal_tax || 0), 0).toLocaleString()}</td>
                        <td className="p-3 text-right font-mono text-red-600">-${paychecks.reduce((s, p) => s + (p.state_tax || 0), 0).toLocaleString()}</td>
                        <td className="p-3 text-right font-mono text-red-600">-${paychecks.reduce((s, p) => s + (p.fica || 0), 0).toLocaleString()}</td>
                        <td className="p-3 text-right font-mono text-red-600">-${paychecks.reduce((s, p) => s + (p.sdi || 0), 0).toLocaleString()}</td>
                        <td className="p-3 text-right font-mono text-emerald-600">${paychecks.reduce((s, p) => s + (p.net_pay || 0), 0).toLocaleString()}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {payRegister && (
            <Card>
              <CardHeader>
                <CardTitle>Pay Register Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <p className="text-2xl font-bold">${(payRegister.total_gross || 0).toLocaleString()}</p>
                    <p className="text-sm text-gray-500">Total Gross</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-600">${(payRegister.total_taxes || 0).toLocaleString()}</p>
                    <p className="text-sm text-gray-500">Total Taxes</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-600">${(payRegister.employer_taxes || 0).toLocaleString()}</p>
                    <p className="text-sm text-gray-500">Employer Taxes</p>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-emerald-600">${(payRegister.total_net || 0).toLocaleString()}</p>
                    <p className="text-sm text-gray-500">Total Net Pay</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setCurrentStep(3)}>Back to Calculate</Button>
            <Button onClick={() => setCurrentStep(5)} disabled={paychecks.length === 0} data-testid="proceed-to-post-btn">
              Continue to Post
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 5: Post */}
      {currentStep === 5 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Post to Annual Records
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 bg-emerald-50 rounded-lg space-y-3">
              <h3 className="font-semibold text-emerald-800">Pre-Post Checklist</h3>
              <div className="space-y-2">
                {[
                  { label: 'All paychecks calculated', done: paychecks.length > 0 },
                  { label: 'Tax calculations verified', done: paychecks.length > 0 },
                  { label: 'Ready to post to employee annual records', done: paychecks.length > 0 }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    {item.done ? (
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-400" />
                    )}
                    <span className={item.done ? 'text-emerald-800' : 'text-gray-400'}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-amber-50 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Important Notice</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Posting will update employee annual YTD records. This action creates permanent payroll history entries.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => setCurrentStep(4)}>Back to Review</Button>
              <Button onClick={postToAnnualRecords} disabled={loading || paychecks.length === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="post-annual-btn">
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Post to Annual Records
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default TransactionsPage;
